import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search as SearchIcon, MapPin, Grid3X3 } from 'lucide-react';
import AdBanner from '@/components/AdBanner';
import InFeedAd from '@/components/InFeedAd';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const tattooStyles = [
  'Tradicional', 'Neo-Tradicional', 'Realismo', 'Blackwork',
  'Dotwork', 'Japonés', 'Tribal', 'Minimalista',
  'Acuarela', 'Geométrico', 'Chicano', 'Lettering'
];

const Search = () => {
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStyle, setSelectedStyle] = useState('');
  const [locationFilter, setLocationFilter] = useState('');

  useEffect(() => {
    const debounce = setTimeout(() => {
      fetchArtists();
    }, 300);
    return () => clearTimeout(debounce);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, selectedStyle, locationFilter]);

  const fetchArtists = async () => {
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('q', searchQuery);
      if (selectedStyle && selectedStyle !== 'all') params.append('style', selectedStyle);
      if (locationFilter) params.append('location', locationFilter);

      const response = await axios.get(`${API}/users/search/artists?${params.toString()}`);
      setArtists(response.data);
    } catch (error) {
      console.error('Error searching artists:', error);
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedStyle('');
    setLocationFilter('');
  };

  return (
    <div className="max-w-6xl mx-auto px-3 sm:px-4 py-6 sm:py-8" data-testid="search-container">
      {/* Header */}
      <div className="text-center mb-8 sm:mb-12">
        <h1 className="font-['Cinzel'] text-2xl sm:text-4xl md:text-5xl font-bold text-white mb-3 sm:mb-4">
          ENCUENTRA TU ARTISTA
        </h1>
        <p className="text-zinc-500 max-w-xl mx-auto text-sm sm:text-base">
          Busca entre nuestra comunidad de tatuadores por nombre, estilo o ubicación
        </p>
      </div>

      {/* Search Filters */}
      <div className="bg-zinc-900/50 border border-zinc-800 p-4 sm:p-6 mb-6 sm:mb-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={18} />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Buscar por nombre..."
              className="pl-10 bg-transparent border-zinc-800 focus-visible:ring-0"
              data-testid="search-input"
            />
          </div>
          <Select value={selectedStyle} onValueChange={setSelectedStyle}>
            <SelectTrigger className="bg-transparent border-zinc-800" data-testid="style-filter">
              <SelectValue placeholder="Estilo de tatuaje" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-800">
              <SelectItem value="all">Todos los estilos</SelectItem>
              {tattooStyles.map((style) => (
                <SelectItem key={style} value={style}>{style}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="relative">
            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={18} />
            <Input
              value={locationFilter}
              onChange={(e) => setLocationFilter(e.target.value)}
              placeholder="Ubicación..."
              className="pl-10 bg-transparent border-zinc-800 focus-visible:ring-0"
              data-testid="location-input"
            />
          </div>
        </div>
        {(searchQuery || selectedStyle || locationFilter) && (
          <button
            onClick={clearFilters}
            className="mt-4 text-sm text-zinc-500 hover:text-white transition-colors"
            data-testid="clear-filters"
          >
            Limpiar filtros
          </button>
        )}
      </div>

      {/* Results */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="bg-zinc-900 border border-zinc-800 p-6 animate-pulse">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-16 h-16 rounded-full bg-zinc-800" />
                <div className="space-y-2">
                  <div className="h-4 w-24 bg-zinc-800" />
                  <div className="h-3 w-16 bg-zinc-800" />
                </div>
              </div>
              <div className="h-3 w-full bg-zinc-800 mb-2" />
              <div className="h-3 w-2/3 bg-zinc-800" />
            </div>
          ))}
        </div>
      ) : artists.length === 0 ? (
        <div className="text-center py-16">
          <SearchIcon className="w-16 h-16 text-zinc-800 mx-auto mb-4" />
          <p className="text-zinc-500 text-lg">No se encontraron artistas</p>
          <p className="text-zinc-600 mt-2">Intenta con otros filtros de búsqueda</p>
        </div>
      ) : (
        <>
          <p className="text-zinc-500 mb-6">
            {artists.length} artista{artists.length !== 1 ? 's' : ''} encontrado{artists.length !== 1 ? 's' : ''}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {artists.map((artist) => (
              <Link
                key={artist.id}
                to={`/profile/${artist.id}`}
                className="bg-zinc-900/50 border border-zinc-800 hover:border-zinc-700 p-6 transition-colors group"
                data-testid={`artist-card-${artist.id}`}
              >
                <div className="flex items-center gap-4 mb-4">
                  <Avatar className="h-16 w-16 border border-zinc-800 group-hover:border-zinc-700 transition-colors">
                    <AvatarImage src={artist.avatar} />
                    <AvatarFallback className="bg-zinc-800 text-zinc-400 text-xl">
                      {artist.name?.charAt(0)?.toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <h3 className="font-['Cinzel'] text-lg font-semibold text-white group-hover:text-zinc-200">
                      {artist.name}
                    </h3>
                    {artist.location && (
                      <p className="flex items-center gap-1 text-sm text-zinc-500">
                        <MapPin size={12} />
                        {artist.location}
                      </p>
                    )}
                  </div>
                </div>

                {artist.bio && (
                  <p className="text-sm text-zinc-500 line-clamp-2 mb-4">{artist.bio}</p>
                )}

                {artist.styles?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-4">
                    {artist.styles.slice(0, 3).map((style) => (
                      <span key={style} className="px-2 py-1 text-xs uppercase tracking-wider bg-zinc-800 text-zinc-400">
                        {style}
                      </span>
                    ))}
                    {artist.styles.length > 3 && (
                      <span className="px-2 py-1 text-xs text-zinc-600">
                        +{artist.styles.length - 3}
                      </span>
                    )}
                  </div>
                )}

                <div className="flex items-center gap-4 pt-4 border-t border-zinc-800/50 text-sm text-zinc-500">
                  <span className="flex items-center gap-1">
                    <Grid3X3 size={14} />
                    {artist.artworks_count || 0} trabajos
                  </span>
                  <span>{artist.followers_count || 0} seguidores</span>
                </div>
              </Link>
            ))}
          </div>
          {/* Ad after search results */}
          <div className="mt-8">
            <AdBanner slot="3497156484" format="horizontal" />
          </div>
        </>
      )}
    </div>
  );
};

export default Search;
